import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, Warning, CheckCircle } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [validity, setValidity] = useState(null); // {valid:bool, email?, reason?}
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const validate = async () => {
      if (!token) {
        setValidity({ valid: false, reason: "missing" });
        setValidating(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/reset-password/validate`, { params: { token } });
        if (active) setValidity(res.data);
      } catch {
        if (active) setValidity({ valid: false, reason: "error" });
      } finally {
        if (active) setValidating(false);
      }
    };
    validate();
    return () => { active = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successful");
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="reset-password-page">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_role-flow-hub/artifacts/2gzvteft_ChatGPT%20Image%20Apr%2021%2C%202026%2C%2010_09_19%20AM.png')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-primary/30" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-md shadow-lg mb-6">
              <Buildings size={32} weight="bold" className="text-primary" />
            </div>
            <h1 className="text-4xl font-black text-white tracking-tight mb-2">CME PeopleHub</h1>
            <p className="text-white/70 text-lg">Set a new password</p>
          </div>

          <div className="bg-white rounded-md shadow-2xl p-8">
            {validating ? (
              <div className="flex items-center gap-3 text-muted-foreground" data-testid="reset-validating">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm">Validating reset link…</p>
              </div>
            ) : !validity?.valid ? (
              <div data-testid="reset-invalid">
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
                  <Warning size={20} className="text-destructive mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-destructive">Invalid or expired link</p>
                    <p className="text-sm text-destructive/80 mt-1">
                      {validity?.reason === "expired"
                        ? "This reset link has expired. Please request a new one."
                        : "This password reset link is no longer valid. Please request a new one from the login page."}
                    </p>
                  </div>
                </div>
                <Button className="w-full" onClick={() => navigate("/login")} data-testid="go-to-login-btn">
                  Back to login
                </Button>
              </div>
            ) : success ? (
              <div data-testid="reset-success" className="text-center py-6">
                <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto mb-3" />
                <p className="text-lg font-semibold mb-2">Password updated</p>
                <p className="text-sm text-muted-foreground">Redirecting to login…</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-foreground mb-2">Choose a new password</h2>
                  <p className="text-muted-foreground text-sm">
                    {validity.email ? <>For <strong>{validity.email}</strong></> : "Enter a new password to regain access."}
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3" data-testid="reset-error">
                    <Warning size={20} className="text-destructive mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                  <div>
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      data-testid="reset-new-password"
                      autoFocus
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password">Confirm password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Retype your new password"
                      data-testid="reset-confirm-password"
                      autoComplete="new-password"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11 font-semibold" disabled={submitting} data-testid="reset-submit-btn">
                    {submitting ? "Saving…" : "Reset password"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
